import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type TourWaveFormSchema, tourWaveFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { TourWaveForm } from "@/forms/tour-wave-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditTourWaveModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	tourWaveId: string;
}

export function EditTourWaveModal({
	onClose,
	isOpen,
	onComplete,
	tourWaveId,
}: EditTourWaveModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["tour-waves"][":tourWaveId"].$get({
			param: { tourWaveId },
		});
		const data = await responseHandler(response);
		return tourWaveFormSchema.parse(data.tourWave);
	});
	const methods = useForm({
		resolver: resolver(tourWaveFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: TourWaveFormSchema) {
		const response = await client.stock["tour-waves"][":tourWaveId"].$patch({
			param: { tourWaveId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_tour_wave")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<TourWaveForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
